import { LocaleString } from '@/api-client/models/utilization/Contracts'
import { Feature } from '@/pinia/feature'
import { RGBA, colorBetween } from '@/utils/rgba'

export const createColorScale = (color: string, from: number, to: number) => ({
  type: 'INTERPOLATE',
  cutoffs: [
    {
      value: from,
      color: colorBetween(RGBA.fromHex('#ffffff'), RGBA.fromHex(color), 0.1).toHex()
    },
    { value: to * 0.5, color: color },
    { value: to, color: colorBetween(RGBA.fromHex('#000000'), RGBA.fromHex(color), 0.6).toHex() }
  ]
})

export const areaFormat = {
  format: {
    type: 'intl-number',
    maximumFractionDigits: 0
  },
  steps: [{ format: { type: 'format-string', format: '%s m²' } }]
}

export const ratioFormat = {
  format: {
    type: 'intl-number',
    style: 'percent',
    maximumFractionDigits: 0
  }
}

export const numberColor = '#6a6a6a'
export const ratioColor = '#40a9ff'
export const enabledEntityLevels = ['REGION', 'COMPLEX', 'REAL_ESTATE', 'CONSTRUCTION']

export const createMeasurement =
  (tab: string) =>
  ({
    name,
    title,
    info,
    first,
    ratio
  }: {
    name: string
    title: LocaleString
    info: LocaleString
    first?: boolean
    ratio?: boolean
  }): Feature => ({
    name,
    title,
    info,
    enabled: true,
    component: 'MainMeasurementCard',
    props: {
      initialShowOnMap: first,
      value: {
        dataSource: `portfolio.measurement.${tab}.${name}.value`,
        textType: 'h4',
        type: ratio ? 'gauge' : 'number',
        format: ratio ? ratioFormat : areaFormat
      },
      coloredEntities: {
        dataSource: `portfolio.measurement.${tab}.${name}.colored-entities`,
        enabledEntityLevels
      },
      colorLegend: {
        format: ratio ? ratioFormat : areaFormat
      },
      colorScale: ratio
        ? createColorScale(ratioColor, 0, 1)
        : createColorScale(numberColor, 0, 100000),
      color: ratio ? undefined : numberColor
    },
    children: ratio
      ? []
      : [
          {
            name: 'legend',
            enabled: true,
            component: 'InteractiveColorLegend',
            title,
            props: {
              fromColor: '#FF4D4F',
              toColor: '#40A9FF',
              middleColor: '#73D13D',
              format: areaFormat
            }
          }
        ]
  })

export const titles: Record<string, LocaleString> = {
  'loa-bta': {
    en: 'Area efficiency (LOA/BTA)',
    sv: 'Yteffektivitet (LOA/BTA)'
  },
  'loah-bta': {
    en: 'Area efficiency (LOA-h/BTA)',
    sv: 'Yteffektivitet (LOA-h/BTA)'
  },
  nta: {
    sv: 'Nettoarea (NTA)',
    en: 'Net area (NTA)'
  },
  bta: {
    sv: 'Bruttoarea (BTA)',
    en: 'Gross area (BTA)'
  },
  bra: {
    sv: 'Bruksarea (BRA)',
    en: 'Usable area (BRA)'
  },
  boa: {
    sv: 'Boarea (BOA)',
    en: 'Living area (BOA)'
  },
  bya: {
    sv: 'Byggnadsarea (BYA)',
    en: 'Building area (BYA)'
  },
  loa: {
    sv: 'Lokalarea (LOA)',
    en: 'Facilities area (LOA)'
  },
  loah: {
    sv: 'Uthyrningsbar area (LOA-h)',
    en: 'Leasable area (LOA-h)'
  }
}

export const infos: Record<string, LocaleString> = {
  nta: {
    sv: 'Nettoarean är den yta som kan användas för sitt avsedda ändamål, exklusive väggar och andra byggnadsdelar.',
    en: 'Net area (NTA) refers to the usable space for its intended purpose, excluding walls and other structural components.'
  },
  bta: {
    sv: 'Bruttoarean inkluderar all yta inom en byggnads omslutande väggar, inklusive väggar och andra byggnadsdelar.',
    en: 'Gross area (BTA) includes all space within a building’s enclosing walls, including walls and other structural components.'
  },
  bra: {
    sv: 'Bruksarea (BRA) begränsas av de omslutande byggnadsdelarnas insida. BRA kan utgöras av  gemensamhetsutrymmen och nyttjandeenheter med varierande användningsområden.',
    en: 'Usable area (BRA) is limited by the inside of the enclosing building components. BRA may consist of common areas and utilization units with varying uses.'
  },
  boa: {
    sv: 'Boarean avser den del av en byggnad som är avsedd för boende, inklusive rum och kök, men exkluderar icke-boende utrymmen som garage och förråd.',
    en: 'Living area (BOA) refers to the part of a building intended for living, including rooms and kitchen, but excluding non-living spaces such as garages and storage areas.'
  },
  bya: {
    sv: 'Byggnadsarean är den totala ytan som en byggnad upptar på marken, inklusive uteplatser och andra angränsande byggnadsdelar.',
    en: 'Building area (BYA) is the total area occupied by a building on the ground, including patios and other adjoining building components.'
  },
  loa: {
    sv: 'Lokalarean avser den yta inom en byggnad som är avsedd för kommersiellt bruk eller offentliga tjänster.',
    en: 'Local area (LOA) refers to the space within a building intended for commercial use or public services.'
  },
  loah: {
    sv: 'Uthyrningsbar area (LOA-h) är den del av lokalarean som kan hyras ut till hyresgäster, ofta exkluderande gemensamma utrymmen som korridorer och toaletter.',
    en: 'Leasable area (LOA-h) is the part of the local area that can be leased to tenants, often excluding common areas such as corridors and restrooms.'
  }
}

export const createMeasurementFromName =
  (tab: string) =>
  (name: string, first?: boolean, ratio?: boolean): Feature =>
    createMeasurement(tab)({
      name,
      title: titles[name],
      info: infos[name],
      first,
      ratio
    })

export const createValueDataSource = (tab: string) => (name: string, index: number) => ({
  id: `portfolio.measurement.${tab}.${name}.value`,
  type: 'fetch',
  fetch: `fetch.portfolio.${tab}.measurements`,
  path: `measurements[${index}].splits[0].value`
})

export const createColoredEntitiesDataSource = (tab: string) => (name: string, index: number) => ({
  id: `portfolio.measurement.${tab}.${name}.colored-entities`,
  type: 'fetch',
  fetch: `fetch.portfolio.${tab}.measurements`,
  transform: {
    id: 'portfolio.apartments.colored-entities',
    index
  }
})
